import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './MusicToggle.css';

const audioSource = '/assets/song.mp3';

export type MusicToggleHandle = {
  play: () => void;
  pause: () => void;
  toggle: () => void;
  show: () => void;
  hide: () => void;
};

const MusicToggle = forwardRef<MusicToggleHandle>(function MusicToggle(_, ref) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = useCallback(() => {
    if (!audioRef.current) {
      return;
    }

    const playAttempt = audioRef.current.play();
    if (playAttempt && typeof playAttempt.then === 'function') {
      playAttempt
        .then(() => setIsPlaying(true))
        .catch(() => {
          setIsPlaying(false);
        });
    } else {
      setIsPlaying(true);
    }
  }, []);

  const pauseAudio = useCallback(() => {
    if (!audioRef.current) {
      return;
    }

    audioRef.current.pause();
    setIsPlaying(false);
  }, []);

  const handleToggle = useCallback(() => {
    if (isPlaying) {
      pauseAudio();
      return;
    }

    playAudio();
  }, [isPlaying, pauseAudio, playAudio]);

  useImperativeHandle(
    ref,
    () => ({
      play: playAudio,
      pause: pauseAudio,
      toggle: handleToggle,
      show: () => setIsVisible(true),
      hide: () => setIsVisible(false),
    }),
    [handleToggle, pauseAudio, playAudio],
  );

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }

    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const audioEl = audioRef.current;
    audioEl.addEventListener('ended', handleEnded);
    audioEl.addEventListener('play', handlePlay);
    audioEl.addEventListener('pause', handlePause);

    return () => {
      audioEl.removeEventListener('ended', handleEnded);
      audioEl.removeEventListener('play', handlePlay);
      audioEl.removeEventListener('pause', handlePause);
    };
  }, []);

  return (
    <>
      {isVisible ? (
        <button
          type="button"
          className="music-toggle"
          onClick={handleToggle}
          aria-label={isPlaying ? 'Pause music' : 'Play music'}
        >
          {isPlaying ? (
            <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
              <path
                d="M4 10v4h4l5 4V6l-5 4H4z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16.5 8.5a5 5 0 0 1 0 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M19 6a8 8 0 0 1 0 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
              <path
                d="M4 10v4h4l5 4V6l-5 4H4z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18 9.5L22 13.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M22 9.5L18 13.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </button>
      ) : null}
      <audio ref={audioRef} src={audioSource} preload="auto" />
    </>
  );
});

export default MusicToggle;
